import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Star } from 'lucide-react';
import { UserProfile } from '@/types/user';
import { ProfileHeader } from '@/components/service-provider/ProfileHeader';
import { SpecialtiesSection } from '@/components/service-provider/SpecialtiesSection';
import { GallerySection } from '@/components/service-provider/GallerySection';
import { RatingDialog } from '@/components/service-provider/RatingDialog';
import { RatingsSection } from '@/components/service-provider/RatingsSection';
import { auth, db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp, query, getDocs, where } from 'firebase/firestore';
import { toast } from 'sonner';

interface ServiceProviderClientProps {
  provider: UserProfile;
}

export default function ServiceProviderClient({ provider }: ServiceProviderClientProps) {
  const navigate = useNavigate();
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [canRate, setCanRate] = useState(true);

  useEffect(() => {
    checkIfCanRate();
  }, []);

  const checkIfCanRate = async () => {
    if (!auth.currentUser || auth.currentUser.uid === provider.id) {
      setCanRate(false);
      return;
    }

    try {
      const ratingsRef = collection(db, `users/${provider.id}/ratings`);
      const q = query(ratingsRef, where('authorId', '==', auth.currentUser.uid));
      const snapshot = await getDocs(q);
      
      setCanRate(snapshot.empty);
    } catch (error) {
      console.error('Error checking if can rate:', error);
      setCanRate(false);
    }
  };

  const handleSubmitRating = async (rating: number, comment: string) => {
    if (!auth.currentUser) {
      toast.error('Você precisa estar logado para avaliar');
      return;
    }

    try {
      const ratingsRef = collection(db, `users/${provider.id}/ratings`);
      await addDoc(ratingsRef, {
        rating,
        comment,
        authorId: auth.currentUser.uid,
        authorName: auth.currentUser.displayName || 'Usuário',
        createdAt: serverTimestamp()
      });

      setCanRate(false);
      toast.success('Avaliação enviada com sucesso!');
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Erro ao enviar avaliação');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <Button
          variant="ghost"
          className="flex items-center gap-2 hover:bg-gray-100"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para Dashboard
        </Button>

        {/* Perfil Principal */}
        <Card className="p-6">
          <div className="flex justify-between items-start mb-6">
            <ProfileHeader provider={provider} />
            {canRate && (
              <Button
                onClick={() => setShowRatingDialog(true)}
                className="flex items-center gap-2"
              >
                <Star className="h-4 w-4" />
                Avaliar Profissional
              </Button>
            )}
          </div>
        </Card>

        {/* Avaliações */}
        <RatingsSection providerId={provider.id} />

        {/* Especialidades */}
        <Card className="p-6">
          <SpecialtiesSection specialties={provider.specialties} />
        </Card>

        {/* Galeria de Serviços */}
        {provider.gallery && provider.gallery.length > 0 && (
          <Card className="p-6">
            <GallerySection gallery={provider.gallery} />
          </Card>
        )}

        {/* Rating Dialog */}
        <RatingDialog
          open={showRatingDialog}
          onOpenChange={setShowRatingDialog}
          onSubmit={handleSubmitRating}
        />
      </div>
    </div>
  );
}